import React from 'react';
import {
  AccountWithHealthZone,
  ACCESS_INFO_SECTIONS,
  DashboardProject,
  HealthZone,
  mainZones,
  Project,
  PROJECT_ZONES_ALIASES,
  recalculateAccountHealthZone,
  ZONE_MAPPING,
  ZONE_PRIORITY,
  GET_PROJECTS_DASHBOARD,
  CREATE_PROJECT,
  PROJECT_FROM_OPPORTUNITY_STATE,
  StatusFilter,
} from 'domain/shared/newProject';
import ProjectDashboardCollapsibleZone from '../zone/ProjectDashboardCollapsibleZone';
import { sortProjectsByZonePriority } from '../hooks/useProjectDashboard';
import { Filters, FiltersLayout, GraphQLList, ListPageLayout, useListContext } from 'framework/list';
import { FilterCondition } from 'framework/types';
import projectDataGetters from '../utils/projectDataGetters';
import { Loading } from 'framework/ui/mui-builder';
import { useAuthorization } from 'framework/authorization';
import { DashboardProvider } from 'framework/ui/dashboard';
import { EmployeeFilter } from 'domain/shared/employee';
import { PROJECT_MEMBERSHIP_ROLES_KEY } from 'domain/shared/core/constants';
import type { ProjectZoneAlias } from '../dashboardTypes';
import { getControlIssues } from '../../shared/mainProjectSection/controlIssues';

type ArchiveFilterValue = boolean | string;
type ArchiveFilter = Partial<
  Record<FilterCondition.EQ | FilterCondition.IN, ArchiveFilterValue | ArchiveFilterValue[]>
>;
const getArchiveFilterValue = (isArchivedFilter?: ArchiveFilter) => {
  const inValue = isArchivedFilter?.[FilterCondition.IN];
  const rawValue = isArchivedFilter?.[FilterCondition.EQ] ?? (Array.isArray(inValue) ? inValue[0] : inValue);
  return rawValue === true || rawValue === 'true';
};

type DashboardSearchFilter = {
  filter: {
    isArchived?: Record<string, boolean>;
    projectState?: { ne: string };
    name?: { ct: string };
    'specification:hasAccountManager'?: { eq: string };
    'specification:hasTeamMember'?: {
      eq: {
        alias: string;
        role: typeof PROJECT_MEMBERSHIP_ROLES_KEY.PROJECT_MANAGER;
      };
    };
  };
};

const ProjectsDashboardWithFilters = () => {
  const { data: { items = [] } = {}, loading } = useListContext();
  const rawAccounts = items as Array<AccountWithHealthZone>;

  const hasProjectsInMainZones = rawAccounts.some(account => {
    const zone = account.accountHealthZone;
    return zone && zone !== PROJECT_ZONES_ALIASES.OTHER;
  });

  const hasProjectsInOther = rawAccounts.some(account => {
    const zone = account.accountHealthZone || PROJECT_ZONES_ALIASES.OTHER;
    return zone === PROJECT_ZONES_ALIASES.OTHER;
  });

  if (loading) {
    return <Loading />;
  }

  return (
    <DashboardProvider dataGetters={projectDataGetters}>
      {hasProjectsInMainZones &&
        mainZones.map(zone => <ProjectDashboardCollapsibleZone key={zone} zone={zone as ProjectZoneAlias} />)}

      {hasProjectsInOther && (
        <ProjectDashboardCollapsibleZone
          key={PROJECT_ZONES_ALIASES.OTHER}
          zone={PROJECT_ZONES_ALIASES.OTHER as ProjectZoneAlias}
        />
      )}
    </DashboardProvider>
  );
};

const ProjectsDashboard = () => {
  const { isGranted } = useAuthorization();
  const transformVariables = (params: any) => {
    const isArchivedFilter = params.filter?.['isArchived'];
    const isArchived = getArchiveFilterValue(isArchivedFilter);
    const projectManager = params.filter?.projectManager?.eq;
    const ownerAlias = params.filter?.ownerAlias?.eq;
    const filterObject: DashboardSearchFilter = {
      filter: {
        isArchived: isArchived ? { [FilterCondition.EQ]: true } : { ne_or_null: true },
        projectState: { ne: PROJECT_FROM_OPPORTUNITY_STATE.PRESTART },
      },
    };

    if (projectManager) {
      filterObject.filter['specification:hasTeamMember'] = {
        eq: {
          alias: projectManager,
          role: PROJECT_MEMBERSHIP_ROLES_KEY.PROJECT_MANAGER,
        },
      };
    }
    if (ownerAlias) {
      filterObject.filter['specification:hasAccountManager'] = {
        eq: ownerAlias,
      };
    }

    if (params.filter?.['specification:search']?.eq) {
      const searchString = params.filter['specification:search'].eq?.trim();
      if (searchString) {
        filterObject.filter.name = { ct: searchString.trim() };
      }
    }

    return {
      searchString: JSON.stringify(filterObject),
    };
  };

  const transformData = async (data: any) => {
    const rawProjects = data || [];
    const accountsMap = new Map<string, AccountWithHealthZone>();
    rawProjects.forEach((project: Project) => {
      const isHealthAvailable =
        project?.accessInfo?.sections?.some(
          v => v.section === ACCESS_INFO_SECTIONS.HEALTH && v.allowedActions.length > 0,
        ) || false;

      const backendZone = isHealthAvailable
        ? project.health?.zone || PROJECT_ZONES_ALIASES.SAFE
        : PROJECT_ZONES_ALIASES.OTHER;
      let mappedZone = ZONE_MAPPING[backendZone] || PROJECT_ZONES_ALIASES.SAFE;
      if (isHealthAvailable) {
        const staffingZone = getControlIssues(project).find(issue => issue.key === 'staffing')?.zone;
        const mappedStaffingZone = staffingZone ? ZONE_MAPPING[staffingZone] : undefined;
        if (mappedStaffingZone && ZONE_PRIORITY[mappedStaffingZone] > ZONE_PRIORITY[mappedZone as HealthZone]) {
          mappedZone = mappedStaffingZone;
        }
      }
      let accountId = `no-account-${project.id}`;
      let accountName = 'No Account';

      if (mappedZone !== PROJECT_ZONES_ALIASES.OTHER && project.accountData) {
        if (project.accountData.accountId) {
          accountId = project.accountData.accountId;
        }
        if (project.accountData.accountName) {
          accountName = project.accountData.accountName;
        }
      }

      const projectWithZone: DashboardProject = {
        ...project,
        projectName: project.name || project.alias || 'Unknown Project',
        projectHealthZone: mappedZone as HealthZone,
        accountId: accountId,
        accountName: accountName,
      };

      if (!accountsMap.has(accountId)) {
        accountsMap.set(accountId, {
          accountId: accountId,
          accountName,
          accountHealthZone: projectWithZone.projectHealthZone,
          projects: [projectWithZone],
        });
      } else {
        const currentAccount = accountsMap.get(accountId);
        currentAccount!.projects.push(projectWithZone);
        currentAccount!.accountHealthZone = recalculateAccountHealthZone(
          currentAccount!.accountHealthZone,
          projectWithZone,
        );
      }
    });

    const sortedAccountsWithProjects = Array.from(accountsMap.values()).map(account => ({
      ...account,
      projects: sortProjectsByZonePriority(account.projects),
    }));

    return {
      result: sortedAccountsWithProjects,
      totalPages: 1,
      totalElements: sortedAccountsWithProjects.length,
    };
  };

  const filter = (
    <Filters>
      <FiltersLayout>
        <EmployeeFilter source="projectManager" label="Project Manager" />
        <EmployeeFilter source="ownerAlias" label="Account Manager" />
        <StatusFilter
          source="isArchived"
          label="Archive"
          dropdownLabel="Archive"
          isSingle
          options={{ true: 'Yes', false: 'No' }}
        />
      </FiltersLayout>
    </Filters>
  );

  return (
    <GraphQLList
      query={GET_PROJECTS_DASHBOARD}
      dataPath="baseProjects"
      transformData={transformData}
      transformVariables={transformVariables}
      defaultFilter={{
        isArchived: { [FilterCondition.EQ]: false },
        projectState: { ne: PROJECT_FROM_OPPORTUNITY_STATE.PRESTART },
      }}
      uncontrolledFilters={['isArchived']}
      shouldSaveUncontrolledFilters={false}
      disableUrlUpdate
    >
      <ListPageLayout sxPaper={{ p: '10px!important' }} filter={isGranted(CREATE_PROJECT) ? filter : undefined}>
        <ProjectsDashboardWithFilters />
      </ListPageLayout>
    </GraphQLList>
  );
};

export default ProjectsDashboard;
